/**
 * BusinessInfoTab Component
 * Configure business contact information
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BusinessInfoSchema, type BusinessInfoFormData, formatPhoneNumber } from '../../schemas/widgetConfigSchema';
import { useUpdateBusinessInfo } from '../../hooks/useWidgetConfig';
import { FormSection, FormField } from './shared/FormSection';
import { Button } from '../ui/button';
import type { WidgetConfiguration } from '../../types/widget-config';

export interface BusinessInfoTabProps {
  config: WidgetConfiguration;
}

/**
 * BusinessInfoTab allows providers to update business contact information
 */
export function BusinessInfoTab({ config }: BusinessInfoTabProps) {
  const updateBusinessInfo = useUpdateBusinessInfo();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<BusinessInfoFormData>({
    resolver: zodResolver(BusinessInfoSchema),
    defaultValues: config.businessInfo,
  });

  const onSubmit = (data: BusinessInfoFormData) => {
    updateBusinessInfo.mutate(data);
  };

  const handlePhoneBlur = () => {
    const phone = watch('contactPhone');
    if (phone) {
      const formatted = formatPhoneNumber(phone);
      setValue('contactPhone', formatted);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Contact Information */}
      <FormSection
        title="Contact Information"
        description="Primary contact details displayed to customers"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Contact Email"
            required
            error={errors.contactEmail?.message}
            htmlFor="contactEmail"
          >
            <input
              {...register('contactEmail')}
              id="contactEmail"
              type="email"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="info@example.com"
            />
          </FormField>

          <FormField
            label="Contact Phone"
            required
            error={errors.contactPhone?.message}
            htmlFor="contactPhone"
          >
            <input
              {...register('contactPhone')}
              id="contactPhone"
              type="tel"
              onBlur={handlePhoneBlur}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="(805) 555-1234"
            />
          </FormField>
        </div>

        <FormField
          label="Website"
          description="Your company website URL"
          error={errors.website?.message}
          htmlFor="website"
        >
          <input
            {...register('website')}
            id="website"
            type="url"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="https://www.example.com"
          />
        </FormField>
      </FormSection>

      {/* Business Location */}
      <FormSection
        title="Business Location"
        description="Physical business address and service hours"
      >
        <FormField
          label="Business Address"
          description="Street address, city, state, ZIP"
          error={errors.businessAddress?.message}
          htmlFor="businessAddress"
        >
          <textarea
            {...register('businessAddress')}
            id="businessAddress"
            rows={3}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="123 Main Street&#10;Paso Robles, CA 93446"
          />
        </FormField>

        <FormField
          label="Business Hours"
          description="Operating hours (e.g., Mon-Fri 8am-5pm)"
          error={errors.businessHours?.message}
          htmlFor="businessHours"
        >
          <input
            {...register('businessHours')}
            id="businessHours"
            type="text"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Mon-Fri 8am-5pm, Sat 9am-2pm"
          />
        </FormField>
      </FormSection>

      {/* License Information */}
      <FormSection
        title="License Information (Optional)"
        description="Business license or certification numbers"
      >
        <FormField
          label="License Number"
          error={errors.licenseNumber?.message}
          htmlFor="licenseNumber"
        >
          <input
            {...register('licenseNumber')}
            id="licenseNumber"
            type="text"
            className="block w-full md:w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="CA-12345"
          />
        </FormField>
      </FormSection>

      {/* Save Button */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button
          type="submit"
          disabled={!isDirty || updateBusinessInfo.isPending}
          className="min-w-[120px]"
        >
          {updateBusinessInfo.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Unsaved Changes Warning */}
      {isDirty && (
        <p className="text-sm text-amber-600" role="status">
          You have unsaved changes
        </p>
      )}
    </form>
  );
}
